import React from 'react';
import { Link } from 'react-router-dom';
import { RotateCcw, ArrowLeft, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

const RefundPolicyPage = () => {
  return (
    <div data-testid="refund-policy-page" className="min-h-screen bg-[#FAFAF8]">
      {/* Header */}
      <section className="bg-white py-12 border-b border-slate-100">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <Link to="/" className="inline-flex items-center text-[#4CAF50] hover:text-[#43A047] mb-6">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Grįžti į pradžią
          </Link>
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-[#F3E5F5] rounded-2xl flex items-center justify-center">
              <RotateCcw className="w-8 h-8 text-[#9B59B6]" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-['Poppins'] font-bold text-[#2D3748]">
                Grąžinimo politika
              </h1>
              <p className="text-[#718096]">Paskutinis atnaujinimas: 2025 m. sausio mėn.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <div className="bg-white rounded-3xl p-8 md:p-12 border border-slate-100 prose prose-slate max-w-none">
            
            {/* Important Notice */}
            <div className="bg-[#FFF3E0] rounded-2xl p-6 flex items-start space-x-4 mb-8">
              <AlertCircle className="w-6 h-6 text-[#FF9F43] flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-[#2D3748] mt-0 mb-2">Svarbu žinoti</h3>
                <p className="text-[#718096] text-sm mb-0">
                  Skaitmeniniai produktai pagal Lietuvos ir ES teisę paprastai negrąžinami, 
                  nes jie pristatomi iš karto ir gali būti nukopijuoti. Tačiau mes vertiname 
                  jūsų pasitenkinimą ir nagrinėjame kiekvieną atvejį individualiai.
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748] mt-0">1. Skaitmeniniai produktai</h2>
            
            <div className="bg-[#FFEBEE] rounded-2xl p-6 my-6">
              <h3 className="text-lg font-semibold text-[#2D3748] flex items-center mb-3 mt-0">
                <XCircle className="w-5 h-5 text-[#FF6B6B] mr-2" />
                Grąžinimas negalimas
              </h3>
              <p className="text-[#718096] text-sm mb-0">
                PDF vadovai, video kursai ir audio failai negrąžinami, nes:
              </p>
              <ul className="text-[#718096] text-sm mt-2 mb-0 space-y-1">
                <li>Produktai pristatomi iš karto po mokėjimo</li>
                <li>Skaitmeninis turinys gali būti nukopijuotas</li>
                <li>Tai atitinka LR Vartotojų teisių apsaugos įstatymo 6.22810 str.</li>
              </ul>
            </div>

            <div className="bg-[#E8F5E9] rounded-2xl p-6 my-6">
              <h3 className="text-lg font-semibold text-[#2D3748] flex items-center mb-3 mt-0">
                <CheckCircle className="w-5 h-5 text-[#4CAF50] mr-2" />
                Išimtiniai atvejai
              </h3>
              <p className="text-[#718096] text-sm mb-0">
                Pinigai gali būti grąžinami šiais atvejais:
              </p>
              <ul className="text-[#718096] text-sm mt-2 mb-0 space-y-1">
                <li>Produktas nebuvo pristatytas dėl techninių problemų</li>
                <li>Produktas iš esmės neatitinka aprašymo</li>
                <li>Produktas sugadintas ir neveikiantis</li>
                <li>Du kartus apmokėtas tas pats užsakymas</li>
              </ul>
            </div>

            <h2 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748]">2. Mokymų ir konsultacijų paslaugos</h2>
            
            <h3 className="text-xl font-semibold text-[#2D3748]">Individualios koučingo sesijos</h3>
            <ul className="text-[#718096] space-y-2">
              <li><strong>Atšaukimas iki 24 val.:</strong> Pilnas pinigų grąžinimas arba perkėlimas</li>
              <li><strong>Atšaukimas per 24 val.:</strong> 50% grąžinimas arba perkėlimas</li>
              <li><strong>Neatvykimas be perspėjimo:</strong> Pinigai negrąžinami</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#2D3748]">Grupiniai mokymai įmonėms</h3>
            <p className="text-[#718096] leading-relaxed">
              Atšaukimo sąlygos aptariamos individualiai sutartyje. Standartinės sąlygos:
            </p>
            <ul className="text-[#718096] space-y-2">
              <li><strong>Atšaukimas iki 14 d.:</strong> Pilnas grąžinimas</li>
              <li><strong>Atšaukimas 7-14 d.:</strong> 50% grąžinimas</li>
              <li><strong>Atšaukimas per 7 d.:</strong> Grąžinimas negalimas</li>
            </ul>

            <h2 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748]">3. Grąžinimo procesas</h2>
            <p className="text-[#718096] leading-relaxed">
              Jei manote, kad turite teisę į pinigų grąžinimą:
            </p>
            <ol className="text-[#718096] space-y-2">
              <li><strong>1 žingsnis:</strong> Parašykite el. laišką adresu info@tavoemocijos.lt</li>
              <li><strong>2 žingsnis:</strong> Nurodykite užsakymo numerį ir grąžinimo priežastį</li>
              <li><strong>3 žingsnis:</strong> Išnagrinėsime jūsų prašymą per 5 darbo dienas</li>
              <li><strong>4 žingsnis:</strong> Jei prašymas patvirtintas, pinigai grąžinami per 14 dienų</li>
            </ol>

            <h2 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748]">4. Grąžinimo būdas</h2>
            <p className="text-[#718096] leading-relaxed">
              Pinigai grąžinami tuo pačiu mokėjimo būdu, kuriuo buvo atliktas pirkinys:
            </p>
            <ul className="text-[#718096] space-y-2">
              <li>Bankiniai pavedimai – į tą pačią sąskaitą</li>
              <li>Mokėjimo kortelės – į tą pačią kortelę</li>
            </ul>
            <p className="text-[#718096] leading-relaxed">
              Grąžinimas gali užtrukti iki 14 darbo dienų, priklausomai nuo jūsų banko.
            </p>

            <h2 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748]">5. Garantija</h2>
            <p className="text-[#718096] leading-relaxed">
              Nors skaitmeniniams produktams netaikoma standartinė grąžinimo teisė, 
              mes garantuojame:
            </p>
            <ul className="text-[#718096] space-y-2">
              <li>Produktų kokybę ir veikimą</li>
              <li>Techninę pagalbą pristatymo problemų atveju</li>
              <li>Pakartotinį produkto išsiuntimą, jei negavote</li>
            </ul>

            <h2 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748]">6. Kontaktai</h2>
            <p className="text-[#718096] leading-relaxed">
              Grąžinimo klausimais kreipkitės:
            </p>
            <ul className="text-[#718096] space-y-2">
              <li><strong>El. paštas:</strong> info@tavoemocijos.lt</li>
              <li><strong>Atsakymo laikas:</strong> iki 2 darbo dienų</li>
            </ul>

            <div className="bg-[#E8F5E9] rounded-2xl p-6 mt-8">
              <p className="text-[#718096] text-sm mb-0">
                <strong>Jūsų pasitenkinimas mums svarbus!</strong> Jei turite bet kokių klausimų 
                ar problemų su įsigytais produktais, susisiekite – visada stengsimės rasti 
                geriausią sprendimą.
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default RefundPolicyPage;
